import { useCallback, useMemo } from "react"
import {
  AlertCircle,
  AlertTriangle,
  Block,
  Button,
  LabelMedium,
  ParagraphSmall,
  PlusCircle,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  selectIsRecoveryModeEnabled,
  selectUpdateLoanAmount,
  selectUpdateStrategy,
  selectUserLoan,
  selectWalletBalances,
  setUpdateStrategy,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import {
  GAS_BUFFER_FOR_BORROW_UPDATE_TRANSACTION,
  useMinimumCollateralRatio,
  useUpdateCollateralizationRatio,
  useUpdatedCollateralBasedOnRatio,
} from "#/shared/hooks"
import { usePosthogBorrowMoreModalEvents } from "#/shared/hooks/posthog-events"
import { BorrowMoreStrategy } from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  getCurrencySymbol,
} from "#/shared/utils"
import { ComponentStepProps } from "#/ui/Modal"

import { BorrowProgress } from "../components"
import BorrowStrategyItem from "../components/BorrowStrategyItem"

export default function UpdateStrategy({
  onClickContinue,
}: ComponentStepProps) {
  const dispatch = useDappDispatch()
  const [css, theme] = useStyletron()

  const isRecoveryModeEnabled = useDappSelector(selectIsRecoveryModeEnabled)

  const {
    emitBorrowMoreStrategyIncreaseCollEvent,
    emitBorrowMoreStrategyReduceRatioEvent,
  } = usePosthogBorrowMoreModalEvents()

  const { mBTC } = useDappSelector(selectWalletBalances)

  const userLoan = useDappSelector(selectUserLoan)
  const updateLoanAmount = useDappSelector(selectUpdateLoanAmount)
  const updateStrategy = useDappSelector(selectUpdateStrategy)

  const { minimumCollateralRatio, minimumCollateralRatioLabel } =
    useMinimumCollateralRatio()

  const {
    prevCollateralizationRatio,
    prevCollateralizationRatioLabel,
    newCollateralizationRatio,
    newCollateralizationRatioLabel,
  } = useUpdateCollateralizationRatio()

  const prevCollateralAmount = useMemo(
    () => bigIntToFloat(userLoan?.collateralAmount ?? 0n, 18, 8),
    [userLoan],
  )

  const {
    updatedCollateralAmountLabel: newCollateralAmount,
    additionalCollateralAmount,
    additionalCollateralAmountLabel,
  } = useUpdatedCollateralBasedOnRatio(floatToBigInt(updateLoanAmount || "0"))

  const hasEnoughBalance =
    mBTC.balance >
    additionalCollateralAmount + GAS_BUFFER_FOR_BORROW_UPDATE_TRANSACTION

  const hasBalanceError =
    updateStrategy === BorrowMoreStrategy.INCREASE_COLLATERAL &&
    !hasEnoughBalance

  const hasInsufficientRatio =
    newCollateralizationRatio !== null &&
    minimumCollateralRatio !== null &&
    newCollateralizationRatio < minimumCollateralRatio

  const hasInsufficientCurrentRatio =
    prevCollateralizationRatio !== null &&
    minimumCollateralRatio !== null &&
    prevCollateralizationRatio < minimumCollateralRatio

  const onStrategyChange = useCallback(
    (strategy: BorrowMoreStrategy) => {
      dispatch(setUpdateStrategy(strategy))
    },
    [dispatch],
  )

  const reduceRatioMessage = useMemo(() => {
    if (isRecoveryModeEnabled) return "Unavailable in recovery mode"

    return hasInsufficientRatio
      ? `Must be ${minimumCollateralRatioLabel}% or higher`
      : "Increased risk"
  }, [hasInsufficientRatio, isRecoveryModeEnabled, minimumCollateralRatioLabel])

  const addCollateralMessage = useMemo(() => {
    if (hasInsufficientCurrentRatio)
      return `Collateral ratio ${prevCollateralizationRatioLabel}%, must be ${minimumCollateralRatioLabel}% or higher`

    return hasEnoughBalance
      ? `Deposit ${additionalCollateralAmountLabel} ${getCurrencySymbol(
          "mBTC",
        )}`
      : "Insufficient funds"
  }, [
    additionalCollateralAmountLabel,
    hasEnoughBalance,
    hasInsufficientCurrentRatio,
    minimumCollateralRatioLabel,
    prevCollateralizationRatioLabel,
  ])

  const onSubmit = useCallback(() => {
    if (updateStrategy === BorrowMoreStrategy.INCREASE_COLLATERAL) {
      emitBorrowMoreStrategyIncreaseCollEvent()
    } else {
      emitBorrowMoreStrategyReduceRatioEvent()
    }
    onClickContinue()
  }, [
    emitBorrowMoreStrategyIncreaseCollEvent,
    emitBorrowMoreStrategyReduceRatioEvent,
    onClickContinue,
    updateStrategy,
  ])

  return (
    <>
      <BorrowProgress step={1} stepsCount={4} />
      <Block
        className={css({
          padding: theme.sizing.scale800,
          paddingTop: 0,
          height: "100%",
          flexDirection: "column",
          display: "flex",
          gap: theme.sizing.scale800,
        })}
      >
        <Block>
          <BorrowStrategyItem
            onChange={() => onStrategyChange(BorrowMoreStrategy.DECREASE_RATIO)}
            isChecked={updateStrategy === BorrowMoreStrategy.DECREASE_RATIO}
            title="Reduce collateral ratio"
            isDisabled={isRecoveryModeEnabled}
            description={
              <ParagraphSmall color={theme.colors.contentSecondary}>
                {prevCollateralizationRatioLabel}% →{" "}
                <Block
                  as="span"
                  color={theme.colors.contentPrimary}
                  className={css({
                    ...theme.typography.LabelSmall,
                  })}
                >
                  {newCollateralizationRatioLabel}%
                </Block>
              </ParagraphSmall>
            }
            status={
              <>
                {hasInsufficientRatio || isRecoveryModeEnabled ? (
                  <AlertCircle
                    color={theme.colors.negative}
                    size={theme.sizing.scale600}
                  />
                ) : (
                  <AlertTriangle
                    color={theme.colors.warning}
                    size={theme.sizing.scale600}
                  />
                )}
                <ParagraphSmall
                  color={
                    hasInsufficientRatio || isRecoveryModeEnabled
                      ? theme.colors.negative
                      : theme.colors.contentPrimary
                  }
                >
                  {reduceRatioMessage}
                </ParagraphSmall>
              </>
            }
          />
          <BorrowStrategyItem
            isDisabled={!hasEnoughBalance || hasInsufficientCurrentRatio}
            onChange={() =>
              onStrategyChange(BorrowMoreStrategy.INCREASE_COLLATERAL)
            }
            isChecked={
              updateStrategy === BorrowMoreStrategy.INCREASE_COLLATERAL
            }
            title="Increase collateral amount"
            description={
              <ParagraphSmall color={theme.colors.contentSecondary}>
                {prevCollateralAmount} <CurrencySymbol token="mBTC" /> →{" "}
                <Block
                  as="span"
                  color={theme.colors.contentPrimary}
                  className={css({
                    ...theme.typography.LabelSmall,
                  })}
                >
                  {newCollateralAmount} <CurrencySymbol token="mBTC" />
                </Block>
              </ParagraphSmall>
            }
            status={
              <>
                {hasEnoughBalance && !hasInsufficientCurrentRatio ? (
                  <PlusCircle
                    color={theme.colors.backgroundAccent}
                    size={theme.sizing.scale600}
                  />
                ) : (
                  <AlertCircle
                    color={theme.colors.negative}
                    size={theme.sizing.scale600}
                  />
                )}
                <ParagraphSmall
                  color={
                    hasEnoughBalance && !hasInsufficientCurrentRatio
                      ? theme.colors.contentPrimary
                      : theme.colors.negative
                  }
                >
                  {addCollateralMessage}
                </ParagraphSmall>
              </>
            }
          />
        </Block>

        <Block
          className={css({
            padding: `${theme.sizing.scale500} ${theme.sizing.scale600}`,
            display: "flex",
            justifyContent: "space-between",
            borderRadius: theme.borders.radius400,
            border: "1px solid",
            borderColor: theme.colors.borderOpaque,
            marginTop: "auto",
          })}
        >
          <LabelMedium>Balance</LabelMedium>
          <LabelMedium
            color={
              hasEnoughBalance
                ? theme.colors.contentPrimary
                : theme.colors.negative
            }
          >
            {bigIntToHumanReadableFormat(
              mBTC.balance,
              Number(mBTC.decimals),
              8,
            )}{" "}
            <CurrencySymbol token="mBTC" />
          </LabelMedium>
        </Block>
        <Button
          size="large"
          onClick={onSubmit}
          disabled={
            !updateStrategy ||
            hasBalanceError ||
            hasInsufficientRatio ||
            hasInsufficientCurrentRatio
          }
        >
          Review
        </Button>
      </Block>
    </>
  )
}
